import * as React from 'react';

const DEFAULT_THRESHOLD_PX = 500;

type ScrollMetrics = { scrollHeight: number; scrollTop: number; clientHeight: number };

type UseFetchMoreOnScrollOptions = {
    hasNextPage: boolean | undefined;
    isFetchingNextPage: boolean;
    fetchNextPage: () => void;
    thresholdPx?: number;
};

/**
 * Triggers `fetchNextPage` once the user scrolls within `thresholdPx` of the
 * bottom, rather than waiting for the last row to become visible.
 * Returns handlers for both a locally-scrolled container (`onContainerScroll`
 * / `checkContainer`) and the window itself (`checkWindow`) — use whichever
 * matches how the list scrolls.
 */
export const useFetchMoreOnScroll = ({ hasNextPage, isFetchingNextPage, fetchNextPage, thresholdPx = DEFAULT_THRESHOLD_PX }: UseFetchMoreOnScrollOptions) => {
    const maybeFetch = React.useCallback(({ scrollHeight, scrollTop, clientHeight }: ScrollMetrics) => {
        if (scrollHeight - scrollTop - clientHeight < thresholdPx && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [hasNextPage, isFetchingNextPage, fetchNextPage, thresholdPx]);

    const checkContainer = React.useCallback((container: HTMLElement | null) => {
        if (!container) return;
        const { scrollHeight, scrollTop, clientHeight } = container;
        maybeFetch({ scrollHeight, scrollTop, clientHeight });
    }, [maybeFetch]);

    const onContainerScroll = React.useCallback((event: React.UIEvent<HTMLElement>) => {
        checkContainer(event.currentTarget);
    }, [checkContainer]);

    const checkWindow = React.useCallback(() => {
        maybeFetch({
            scrollHeight: document.documentElement.scrollHeight,
            scrollTop: window.scrollY,
            clientHeight: window.innerHeight,
        });
    }, [maybeFetch]);

    return { onContainerScroll, checkContainer, checkWindow };
};
